import React, { lazy, Suspense, useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import "./App.scss";
import portfolioData from "./portfolioData";

const ResumeCanvasViewer = lazy(() => import("./components/resume/ResumeCanvasViewer"));

const navItems = [
  { id: "projects", label: "Projects" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "education", label: "Education" },
  { id: "opportunities", label: "Work" },
  { id: "contact", label: "Contact" }
];

const resumeRoute = "/resume";

const resolveAssetUrl = url => {
  if (!url) {
    return "";
  }

  if (/^(?:[a-z]+:)?\/\//i.test(url)) {
    return url;
  }

  const basePath = process.env.PUBLIC_URL || "";
  const normalizedPath = url.startsWith("/") ? url : `/${url}`;

  return `${basePath}${normalizedPath}`;
};

const resolveHashRoute = hash => {
  const cleanedHash = (hash || "").replace(/^#/, "");
  return cleanedHash.startsWith("/") ? cleanedHash : "/";
};

function App() {
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
  const [hashRoute, setHashRoute] = useState(() => resolveHashRoute(window.location.hash));
  const {
    profile,
    quickFacts,
    skills,
    projects,
    experience,
    education,
    opportunities
  } = portfolioData;

  const resumeUrl = resolveAssetUrl(profile.resumeUrl);
  const resumeViewerHref = `#${resumeRoute}`;
  const hasResume = Boolean(resumeUrl.trim());
  const isResumeRoute = hashRoute === resumeRoute;
  const visibleLinks = profile.links.filter(link => /^https?:\/\//i.test(link.url));
  const initials = profile.shortName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join("");

  useEffect(() => {
    const handleHashChange = () => {
      setHashRoute(resolveHashRoute(window.location.hash));
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      setIsScrollTopVisible(scrollTop > 260);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isResumeRoute) {
    return (
      <div className="wiki-shell">
        <header className="wiki-topbar">
          <div className="container topbar-inner">
            <div className="wordmark">
              <p className="wordmark-label">Resume</p>
              <p className="wordmark-name">{profile.shortName}</p>
            </div>

            <nav className="resume-viewer-links" aria-label="Resume actions">
              <a href="#/">Go Back</a>
              {hasResume ? (
                <>
                  <a href={resumeUrl} download>
                    Download
                  </a>
                </>
              ) : null}
            </nav>
          </div>
        </header>

        <main className="container resume-viewer-main">
          <section className="resume-viewer-panel" aria-label={`${profile.name} resume`}>
            {hasResume ? (
              <Suspense fallback={<p className="resume-fallback">Loading resume viewer...</p>}>
                <ResumeCanvasViewer pdfUrl={resumeUrl} title={`${profile.name} resume`} />
              </Suspense>
            ) : (
              <p className="resume-fallback">
                Resume file is not configured. Add a valid `profile.resumeUrl` in `src/portfolioData.js`.
              </p>
            )}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="wiki-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <header className="wiki-topbar">
        <div className="container topbar-inner">
          <div className="wordmark">
            <p className="wordmark-label">My Workspace</p>
            <p className="wordmark-name">{profile.shortName}</p>
          </div>

          <nav className="section-nav" aria-label="Primary">
            {navItems.map(item => (
              <a key={item.id} href={`#${item.id}`}>
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main id="main-content" className="container wiki-layout">
        <aside className="profile-rail" aria-label="Profile rail">
          <section className="rail-card identity-card">
            <div className="identity-mark" aria-hidden="true">
              {initials}
            </div>
            <div>
              <h1>{profile.name}</h1>
              <p className="identity-headline">{profile.headline}</p>
            </div>
            <div className="status-list">
              <span className="status-pill status-pill--primary">In School</span>
              <span className="status-pill">Open to work</span>
            </div>
          </section>

          <section className="rail-card">
            <h2>Profile Snapshot</h2>
            <dl className="rail-meta">
              <div>
                <dt>Location</dt>
                <dd>{profile.location}</dd>
              </div>
              <div>
                <dt>Degree</dt>
                <dd>{profile.degree}</dd>
              </div>
              <div>
                <dt>Graduation</dt>
                <dd>{profile.graduation}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{profile.email}</dd>
              </div>
            </dl>
          </section>

          <section className="rail-card">
            <h2>Links</h2>
            <ul className="rail-links">
              {visibleLinks.map(link => (
                <li key={link.label}>
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
              {hasResume ? (
                <li>
                  <a href={resumeViewerHref}>
                    Resume
                  </a>
                </li>
              ) : null}
            </ul>
          </section>
        </aside>

        <div className="content-stack">
          <section className="panel intro-panel">
            <div className="intro-copy">
              <p className="panel-label">Overview</p>
              <h2>What I am building now</h2>
              <p>{profile.summary}</p>
              <p>{profile.schoolStatus}</p>
            </div>

            <div className="signal-board" aria-label="Quick highlights">
              {quickFacts.map(fact => (
                <article key={fact.label} className="signal-item">
                  <h3>{fact.label}</h3>
                  <p>{fact.value}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="projects" className="panel">
            <header className="panel-header">
              <p className="panel-label">Project Dossiers</p>
              <h2>Featured Projects</h2>
            </header>

            <div className="project-grid">
              {projects.map(project => (
                <article key={project.name} className="project-dossier">
                  <header className="project-head">
                    <div>
                      <h3>{project.name}</h3>
                      <p className="project-meta">
                        {project.type} | {project.period}
                      </p>
                    </div>
                    <span className="project-status">{project.status}</span>
                  </header>

                  <p className="project-summary">{project.summary}</p>

                  <h4>Key Contributions</h4>
                  <ul>
                    {project.highlights.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>

                  <h4>Tech Stack</h4>
                  <div className="chip-row">
                    {project.stack.map(item => (
                      <span key={item} className="chip">
                        {item}
                      </span>
                    ))}
                  </div>

                  <h4>Signals</h4>
                  <ul>
                    {project.evidence.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  <div className="project-links">
                    {project.links.map(link => (
                      <a key={link.label} href={link.url} target="_blank" rel="noreferrer">
                        {link.label}
                      </a>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section id="skills" className="panel">
            <header className="panel-header">
              <p className="panel-label">Capability Map</p>
              <h2>Skills</h2>
            </header>

            <div className="skills-map">
              {skills.map(skillGroup => (
                <article key={skillGroup.group} className="skill-cluster">
                  <h3>{skillGroup.group}</h3>
                  <ul>
                    {skillGroup.items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section className="split-row">
            <section id="experience" className="panel">
              <header className="panel-header">
                <p className="panel-label">Track Record</p>
                <h2>Experience</h2>
              </header>

              {experience.map(item => (
                <article key={item.role} className="timeline-card">
                  <header>
                    <h3>{item.role}</h3>
                    <p>{item.org}</p>
                    <p className="timeline-period">{item.period}</p>
                  </header>
                  <ul>
                    {item.bullets.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </section>

            <section id="education" className="panel">
              <header className="panel-header">
                <p className="panel-label">Academic</p>
                <h2>Education</h2>
              </header>

              <article className="timeline-card">
                <header>
                  <h3>{education.school}</h3>
                  <p>{education.degree}</p>
                  <p className="timeline-period">{education.timeline}</p>
                </header>

                <p>{education.notes}</p>

                <h4>Relevant Coursework</h4>
                <ul>
                  {education.coursework.map(course => (
                    <li key={course}>{course}</li>
                  ))}
                </ul>
              </article>
            </section>
          </section>

          <section id="opportunities" className="panel opportunity-panel">
            <header className="panel-header">
              <p className="panel-label">Availability</p>
              <h2>{opportunities.title}</h2>
            </header>
            <p>{opportunities.intro}</p>
            <ul className="opportunity-list">
              {opportunities.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section id="contact" className="panel contact-panel">
            <header className="panel-header">
              <p className="panel-label">Contact</p>
              <h2>Get in touch</h2>
            </header>

            <div className="contact-grid">
              <article>
                <p>
                  I am open to software engineering opportunities and collaborations. If you think there is a fit,
                  feel free to reach out.
                </p>
                <ul>
                  <li>Email: {profile.email}</li>
                  <li>Phone: {profile.phone}</li>
                </ul>
              </article>

              <article>
                <h3>Direct links</h3>
                <ul>
                  {visibleLinks.map(link => (
                    <li key={`contact-${link.label}`}>
                      <a href={link.url} target="_blank" rel="noreferrer">
                        {link.label}
                      </a>
                    </li>
                  ))}
                  {hasResume ? (
                    <li>
                      <a href={resumeViewerHref}>
                        Resume
                      </a>
                    </li>
                  ) : null}
                </ul>
              </article>
            </div>
          </section>
        </div>
      </main>

      <button
        type="button"
        className={`scroll-top-button ${isScrollTopVisible ? "is-visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={18} strokeWidth={2.2} aria-hidden="true" />
      </button>

      <footer className="wiki-footer">
        <div className="container">
          <p>{profile.shortName} | Workspace</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
